import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { errText } from "@/admin/api";
import { resourceApi, type ResourceSchema, type Row } from "@/admin/resources/api";
import { FieldInput, FieldValue } from "@/admin/resources/fields";
import { useSchema } from "@/admin/resources/ResourceListPage";
import { CallDetail } from "@/admin/resources/CallDetail";
import { PlanParts } from "@/admin/resources/PlanParts";

export default function ResourceDetailPage() {
  const { resource = "", id = "" } = useParams();
  const schema = useSchema(resource);
  const creating = id === "new";
  const row = useQuery({
    queryKey: ["admin", "row", resource, id],
    queryFn: () => resourceApi.get(resource, id),
    enabled: !creating,
  });

  if (schema.isLoading || (!creating && row.isLoading)) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (!schema.data || (!creating && !row.data)) {
    return <p className="text-sm text-muted-foreground">Not found.</p>;
  }
  return <Detail key={id} schema={schema.data} row={creating ? null : row.data!} />;
}

function titleOf(schema: ResourceSchema, row: Row | null) {
  if (!row) return `New ${schema.singular.toLowerCase()}`;
  for (const k of ["name", "number", "to_number", "event_type", "action", "description"]) {
    const v = row[k];
    if (typeof v === "string" && v) return v;
  }
  return schema.singular;
}

function Detail({ schema, row }: { schema: ResourceSchema; row: Row | null }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const editable = schema.fields.filter((f) => f.edit);
  const readonly = schema.fields.filter((f) => !f.edit);
  const initial = () => Object.fromEntries(editable.map((f) => [f.name, row ? row[f.name] : f.type === "bool" ? f.name === "is_active" : null]));
  const [form, setForm] = useState<Record<string, unknown>>(initial);
  const [confirmDelete, setConfirmDelete] = useState(false);
  useEffect(() => setForm(initial()), [row]); // eslint-disable-line react-hooks/exhaustive-deps

  const changed = Object.fromEntries(
    Object.entries(form).filter(([k, v]) => !row || JSON.stringify(row[k] ?? null) !== JSON.stringify(v ?? null)),
  );
  const dirty = Object.keys(changed).length > 0;

  const refresh = (saved: Row) => {
    qc.setQueryData(["admin", "row", schema.key, saved.id], saved);
    qc.invalidateQueries({ queryKey: ["admin", "rows", schema.key] });
  };

  const save = useMutation({
    mutationFn: () =>
      row
        ? resourceApi.update(schema.key, row.id, changed)
        : resourceApi.create(
            schema.key,
            Object.fromEntries(Object.entries(form).filter(([, v]) => v !== null && v !== undefined)),
          ),
    onSuccess: (saved) => {
      refresh(saved);
      toast.success(row ? "Saved." : `${schema.singular} created.`);
      if (typeof saved.__notice === "string") toast.warning(saved.__notice);
      if (!row) navigate(`/admin/${schema.key}/${saved.id}`, { replace: true });
    },
    onError: (e) => toast.error(errText(e, "Couldn't save.")),
  });

  const remove = useMutation({
    mutationFn: () => resourceApi.remove(schema.key, row!.id),
    onSuccess: () => {
      toast.success("Deleted.");
      qc.invalidateQueries({ queryKey: ["admin", "rows", schema.key] });
      navigate(`/admin/${schema.key}`);
    },
    onError: (e) => toast.error(errText(e, "Couldn't delete.")),
  });

  const action = useMutation({
    mutationFn: (a: string) => resourceApi.action(schema.key, row!.id, a),
    onSuccess: (saved) => {
      refresh(saved);
      toast.success("Done.");
    },
    onError: (e) => toast.error(errText(e)),
  });

  return (
    <div className="space-y-6">
      <div>
        <Link to={`/admin/${schema.key}`} className="inline-flex items-center text-sm text-muted-foreground hover:underline">
          <ArrowLeft className="mr-1 h-4 w-4" /> {schema.title}
        </Link>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold tracking-tight">{titleOf(schema, row)}</h1>
          {row && (
            <div className="flex gap-2">
              {schema.actions.map((a) => (
                <Button key={a.key} variant="outline" disabled={action.isPending} onClick={() => action.mutate(a.key)}>
                  {a.label}
                </Button>
              ))}
              {schema.delete && (
                <Button variant="outline" onClick={() => setConfirmDelete(true)}>
                  <Trash2 className="mr-2 h-4 w-4" /> Delete
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {editable.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{row ? "Edit" : "Details"}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              {editable.map((f) => (
                <div
                  key={f.name}
                  className={f.type === "longtext" || f.type === "json" ? "space-y-1.5 md:col-span-2" : "space-y-1.5"}
                >
                  <Label>{f.label}</Label>
                  <FieldInput
                    field={f}
                    value={form[f.name]}
                    row={row}
                    onChange={(v) => setForm((s) => ({ ...s, [f.name]: v }))}
                  />
                  {f.help && <p className="text-xs text-muted-foreground">{f.help}</p>}
                </div>
              ))}
            </div>
            <div className="flex justify-end">
              <Button disabled={(row !== null && !dirty) || save.isPending} onClick={() => save.mutate()}>
                {save.isPending ? "Saving…" : row ? "Save" : `Create ${schema.singular.toLowerCase()}`}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {row && readonly.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{editable.length > 0 ? "More" : "Details"}</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="divide-y text-sm">
              {readonly.map((f) => (
                <div
                  key={f.name}
                  className={
                    f.type === "json" || f.type === "longtext"
                      ? "space-y-2 py-3"
                      : "flex items-start justify-between gap-6 py-2"
                  }
                >
                  <dt className="shrink-0 text-muted-foreground">{f.label}</dt>
                  <dd className={f.type === "json" || f.type === "longtext" ? "" : "text-right"}>
                    <FieldValue field={f} row={row} full />
                  </dd>
                </div>
              ))}
            </dl>
          </CardContent>
        </Card>
      )}

      {row && schema.key === "calls" && <CallDetail row={row} />}
      {row && schema.key === "plans" && <PlanParts planId={row.id} />}

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this {schema.singular.toLowerCase()}?</AlertDialogTitle>
            <AlertDialogDescription>This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => remove.mutate()}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
